/* -----------------UI--------------- */

/* -----------------Helpers & Hooks--------------- */
import { CommandBarProvider } from "@features/command-bar/CommandBarProvider";
import { ExamsFilterDialog } from "components/ExamsFilterDialog";
import { useRouter, useSegments } from "expo-router";
import { useRegisterActions } from "kbar";

import { events } from "@constants/tracking";
import { useThemeContext } from "@contexts/ThemeContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { trackCustomEvent } from "analytics";
/* -----------------Types--------------- */
import type { Action } from "kbar";
import { useState } from "react";

const CommandBarActions = () => {
	const router = useRouter();

	const { toggleDarkMode } = useThemeContext();

	const {
		currentExamContext: { practiceTestProductNumericId },
	} = useExamContext();

	const handleTracking = (option: string) => {
		trackCustomEvent({
			eventName: events.commandBarOptionClicked,
			option,
		});
	};

	const [showExamsFilterDialog, setShowExamsFilterDialog] = useState(false);

	const toggleShowExamsFilterDialog = () => {
		setShowExamsFilterDialog((prev) => !prev);
	};

	const actions: Action[] = [
		{
			name: "Home",
			icon: "home-outline",
			keywords: "home, h",
			perform: () => {
				handleTracking("home");
				router.push("/(protected)/(tabs)/home");
			},
			id: "home",
		},
		{
			name: "Progress",
			icon: "chart-timeline-variant-shimmer",
			keywords: "progress, p",
			perform: () => {
				handleTracking("progress");
				router.push("/(protected)/(tabs)/progress");
			},
			id: "progress",
		},
		{
			name: "Change exam",
			icon: "circle-edit-outline",
			keywords: "change exam, ce",
			perform: () => {
				handleTracking("change_exam");
				toggleShowExamsFilterDialog();
			},
			id: "exams",
		},
		{
			name: "Change theme",
			icon: "theme-light-dark",
			keywords: "change theme, ct",
			perform: () => {
				handleTracking("change_theme");
				toggleDarkMode();
			},
			id: "theme",
		},
		{
			name: "Profile",
			icon: "account-outline",
			keywords: "profile, pf",
			perform: () => {
				handleTracking("profile");
				router.push("/profile");
			},
			id: "profile",
		},
		{
			name: "Store",
			icon: "cart-outline",
			keywords: "upgrade, store, u",
			perform: () => {
				handleTracking("store");
				router.push("/store");
			},
			id: "store",
		},
		{
			name: "Leaderboard",
			icon: "trophy-outline",
			keywords: "leaderboard, l",
			perform: () => {
				handleTracking("leaderboard");
				router.push("/(protected)/(tabs)/leaderboard");
			},
			id: "leaderboard",
		},
		{
			name: "Redeem access code",
			icon: "gift-outline",
			keywords: "redeem, r",
			perform: () => {
				handleTracking("redeem");
				router.push("/redeem");
			},
			id: "redeem",
		},
		...(practiceTestProductNumericId
			? [
					{
						name: "Take assessment",
						icon: "clipboard-check-outline",
						keywords: "assessment, ta",
						section: "Study",
						perform: () => {
							handleTracking("take_assessment");
							router.push("/practice-tests");
						},
						id: "assesment",
					},
				]
			: []),
		{
			name: "Settings",
			icon: "cog-outline",
			keywords: "settings, s",
			section: "Account",
			perform: () => {
				handleTracking("settings");
				router.push("/account/settings");
			},
			id: "settings",
		},
	];
	useRegisterActions(actions);
	return (
		<>
			{showExamsFilterDialog && (
				<ExamsFilterDialog
					visible={showExamsFilterDialog}
					handleClose={toggleShowExamsFilterDialog}
				/>
			)}
		</>
	);
};
export const CommandBarProviderWrapper = ({
	children,
}: {
	children: React.ReactElement;
}) => {
	const { isDarkMode } = useThemeContext();
	const segments = useSegments();
	const isTabsLayoutActive = (segments as string[]).includes("(tabs)");

	if (!isTabsLayoutActive) {
		return children;
	}

	return (
		<CommandBarProvider>
			<CommandBarActions
				key={isDarkMode ? isDarkMode.toString() : "command-bar"}
			/>
			{children}
		</CommandBarProvider>
	);
};
